import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { currentMonthBounds, currentWeekBounds } from '../../../utils/employeeRoutes';

export type PeriodGranularity = 'month' | 'week' | 'custom';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(ymd: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function monthBounds(ref: Date): { from: string; to: string } {
  const from = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const to = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  return { from: toYmd(from), to: toYmd(to) };
}

function shiftBounds(
  from: string,
  to: string,
  granularity: PeriodGranularity,
  direction: -1 | 1,
): { from: string; to: string } {
  const start = parseYmd(from);
  const end = parseYmd(to);
  if (!start || !end) return monthBounds(new Date());

  if (granularity === 'week') {
    start.setDate(start.getDate() + direction * 7);
    end.setDate(end.getDate() + direction * 7);
    return { from: toYmd(start), to: toYmd(end) };
  }

  if (granularity === 'month') {
    const anchor = new Date(start.getFullYear(), start.getMonth() + direction, 1);
    return monthBounds(anchor);
  }

  const startMs = start.getTime();
  const endMs = end.getTime();
  const spanDays = Math.max(1, Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1);
  start.setDate(start.getDate() + direction * spanDays);
  end.setDate(end.getDate() + direction * spanDays);
  return { from: toYmd(start), to: toYmd(end) };
}

export function useEmployeePeriodQuery(defaultGranularity: PeriodGranularity = 'month') {
  const [searchParams, setSearchParams] = useSearchParams();
  const [granularity, setGranularityState] = React.useState<PeriodGranularity>(defaultGranularity);

  const defaults = React.useMemo(
    () => (defaultGranularity === 'week' ? currentWeekBounds() : currentMonthBounds()),
    [defaultGranularity],
  );

  const from = searchParams.get('from') || defaults.from;
  const to = searchParams.get('to') || defaults.to;

  const setPeriod = React.useCallback(
    (next: { from: string; to: string }, nextGranularity?: PeriodGranularity) => {
      if (nextGranularity) setGranularityState(nextGranularity);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('from', next.from);
          p.set('to', next.to);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const shift = React.useCallback(
    (direction: -1 | 1) => {
      setPeriod(shiftBounds(from, to, granularity, direction));
    },
    [from, to, granularity, setPeriod],
  );

  const onGranularityChange = React.useCallback(
    (g: 'month' | 'week') => {
      setGranularityState(g);
      if (g === 'week') {
        setPeriod(currentWeekBounds(), 'week');
      } else {
        setPeriod(monthBounds(new Date()), 'month');
      }
    },
    [setPeriod],
  );

  const applyCustomPeriod = React.useCallback(
    (next: { from: string; to: string }) => {
      setPeriod(next, 'custom');
    },
    [setPeriod],
  );

  React.useEffect(() => {
    if (!searchParams.get('from') || !searchParams.get('to')) {
      setPeriod(defaults);
    }
  }, [searchParams, defaults, setPeriod]);

  return {
    from,
    to,
    granularity,
    setGranularity: onGranularityChange,
    shift,
    setPeriod,
    applyCustomPeriod,
  };
}
